"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListScoreRatingConfigsQuerystring = exports.CalculateScoresResponse = exports.CalculateScoresInput = exports.PartnerScoreWithRatingsTransport = exports.PartnerScoreRatingTransport = exports.PartnerScoreTransport = exports.ScoreRatingConfigTransport = exports.ScoreWindowType = exports.ScoreWindowTypes = exports.ScoreLabel = exports.ScoreLabels = exports.ScoreServiceType = exports.ScoreServiceTypes = void 0;
const typebox_1 = require("@sinclair/typebox");
const common_1 = require("../common");
const service_line_keys_1 = require("../operations/service-line-keys");
/**
 * SourScore (Partner Performance Score) storage-schema transports — ENG-551.
 *
 * Scope is the storage schema only: the rubric config (`score_rating_config`) and the
 * calculated current-state scores (`partner_score` + `partner_score_rating`). The
 * calculation engine, V2 routes, and frontend are owned by PRO-127.
 */
/**
 * A score is computed independently per service type; values match the rating `code` prefixes.
 * Scoring applies to two of the four service lines — values are sourced from the canonical
 * `SERVICE_LINE_KEYS` (source of truth: the `svc_line` table) so they can't drift.
 */
exports.ScoreServiceTypes = [service_line_keys_1.SERVICE_LINE_KEYS.ROLL_OFF, service_line_keys_1.SERVICE_LINE_KEYS.COMMERCIAL];
exports.ScoreServiceType = typebox_1.Type.Union(exports.ScoreServiceTypes.map((v) => typebox_1.Type.Literal(v)));
/** Category band label for a composite score. Band thresholds are engine constants (PRO-127). */
exports.ScoreLabels = ['OUTSTANDING', 'STRONG', 'STABLE', 'NEEDS_IMPROVEMENT', 'AT_RISK'];
exports.ScoreLabel = typebox_1.Type.Union(exports.ScoreLabels.map((v) => typebox_1.Type.Literal(v)));
/**
 * How a rating's measurement window is bounded — also the engine's behavioral discriminator
 * for which calculation path to run:
 * - `TIME_BASED` — a calendar window (e.g. last 90 days); `windowValue` = days.
 * - `COUNT_BASED` — a rolling window of the last N events; `windowValue` = N. Any adaptive
 *   sizing/smoothing (e.g. the ROLL_OFF volume tiers) or extension (e.g. "or the last 4 months")
 *   is engine logic, not stored — the DB carries only the headline count.
 * - `ACCELERATOR` — not a weighted rating; an additive, pre-cap bonus (e.g. Net-30). The engine
 *   excludes these from `Σ(normalized × weight)` and adds them after, then caps at 5.0.
 * - `AWARDED` — not computed; the engine short-circuits to a full (100%) normalized score.
 *   Home for the "free 5%" `ROLL_OFF_ACCEPTANCE` rating (weight 0.05, granted to every hauler).
 *   Avoids a separate `award_full` flag — `window_type` stays the single source of truth.
 */
exports.ScoreWindowTypes = ['TIME_BASED', 'COUNT_BASED', 'ACCELERATOR', 'AWARDED'];
exports.ScoreWindowType = typebox_1.Type.Union(exports.ScoreWindowTypes.map((v) => typebox_1.Type.Literal(v)));
/**
 * Response schema for `score_rating_config` — admin-editable, effective-dated rubric rows
 * (and `ACCELERATOR` rows). Edits insert a new version and close the prior row's `effectiveEnd`;
 * rows are never mutated in place, so a `partner_score_rating` FK to a row is always reproducible.
 */
exports.ScoreRatingConfigTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    serviceType: exports.ScoreServiceType,
    /** e.g. `ROLL_OFF_QUALITY`, `ROLL_OFF_NET30_ACCELERATOR`. Not globally unique — one active version per (serviceType, code). */
    code: typebox_1.Type.String(),
    name: typebox_1.Type.String(),
    description: typebox_1.Type.Union([typebox_1.Type.String(), typebox_1.Type.Null()]),
    /** Composite weight (0.3 = 30%); `0` = display-only. For ACCELERATOR rows: the flat additive bonus amount (e.g. 0.10). */
    weight: typebox_1.Type.Number(),
    minRequirement: typebox_1.Type.Union([typebox_1.Type.Number(), typebox_1.Type.Null()]),
    /** Defaults to `minRequirement`. */
    normalizationFloor: typebox_1.Type.Union([typebox_1.Type.Number(), typebox_1.Type.Null()]),
    windowType: exports.ScoreWindowType,
    /**
     * Window size(s), descending. One element = a fixed window (`[90]` days for TIME_BASED,
     * `[20]` events for COUNT_BASED); multiple = tiered COUNT_BASED (`[50, 20]` — the engine uses the
     * largest value ≤ activity, the exact count below the smallest, and Bayesian smoothing kicks in
     * automatically when events < window). "Tiered" is derived as `length > 1`. `null` = lifetime,
     * and `null` for ACCELERATOR / AWARDED.
     */
    windowValue: typebox_1.Type.Union([typebox_1.Type.Array(typebox_1.Type.Integer()), typebox_1.Type.Null()]),
    sortOrder: typebox_1.Type.Number(),
    /** True = a CSA can manually report this rating from the violation form (quality / billing accuracy). */
    manuallyReportable: typebox_1.Type.Boolean(),
    /** When this version became active. */
    effectiveStart: typebox_1.Type.String({ format: 'date-time' }),
    /** Null = currently active; set = superseded/retired. */
    effectiveEnd: typebox_1.Type.Union([typebox_1.Type.String({ format: 'date-time' }), typebox_1.Type.Null()]),
    /** Derived (not stored): a rating is display-only iff `weight === 0`. */
    displayOnly: typebox_1.Type.Boolean(),
    metadata: common_1.MetaData,
});
/**
 * Response schema for `partner_score` — the live, upserted-in-place header.
 * One row per (haulerId, serviceType). Change history is delegated to `audit-trail`.
 */
exports.PartnerScoreTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    haulerId: typebox_1.Type.String({ format: 'uuid' }),
    serviceType: exports.ScoreServiceType,
    /** Final, capped at 5.00 (includes the accelerator bonus when earned). */
    compositeScore: typebox_1.Type.Number(),
    scoreLabel: exports.ScoreLabel,
    /** Orders/lifts in window (volume tier). */
    activityCount: typebox_1.Type.Number(),
    /** When last recomputed — freshness, and the anchor for deriving rating windows. */
    lastScoredAt: typebox_1.Type.String({ format: 'date-time' }),
    metadata: common_1.MetaData,
});
/**
 * Response schema for `partner_score_rating` — the live, upserted-in-place breakdown.
 * One row per rating per score, plus one always-written accelerator row.
 */
exports.PartnerScoreRatingTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    partnerScoreId: typebox_1.Type.String({ format: 'uuid' }),
    /** The rating "type", e.g. `ROLL_OFF_QUALITY`. */
    ratingCode: typebox_1.Type.String(),
    /** FK to the immutable, effective-dated config version used → reproducibility + window/displayOnly derivation. */
    scoreRatingConfigId: typebox_1.Type.String({ format: 'uuid' }),
    /** Raw rate 0–1 (null for display-only / accelerator). */
    rawValue: typebox_1.Type.Union([typebox_1.Type.Number(), typebox_1.Type.Null()]),
    /** 0–100 scale. */
    normalizedValue: typebox_1.Type.Union([typebox_1.Type.Number(), typebox_1.Type.Null()]),
    /** Contribution to composite. Accelerator: the applied bonus — `0` when not earned, the bonus amount when earned. */
    weightedValue: typebox_1.Type.Union([typebox_1.Type.Number(), typebox_1.Type.Null()]),
    /** Events in window; for display-only ratings this is the displayed count. */
    sampleSize: typebox_1.Type.Number(),
    /**
     * Derived (not stored): resolved from the linked config version's `weight === 0` —
     * NOT from this row's `weightedValue`, which can be 0 for non-display reasons.
     */
    displayOnly: typebox_1.Type.Boolean(),
    metadata: common_1.MetaData,
});
/**
 * A score header with its breakdown rows — the shape the engine returns and the read route serves.
 * `ratings` is ordered by the config `sortOrder`, including the always-written accelerator row.
 */
exports.PartnerScoreWithRatingsTransport = typebox_1.Type.Composite([
    exports.PartnerScoreTransport,
    typebox_1.Type.Object({ ratings: typebox_1.Type.Array(exports.PartnerScoreRatingTransport) }),
]);
/**
 * Body for `POST /v2/scores/calculate`. Omit `haulerId` to recompute every eligible hauler
 * (the daily-cron path); omit `serviceType` to compute all scored service types for the hauler.
 * Only `ROLL_OFF` has live inputs today (commercial is blocked on PRO-133/134).
 */
exports.CalculateScoresInput = typebox_1.Type.Object({
    haulerId: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    serviceType: typebox_1.Type.Optional(exports.ScoreServiceType),
});
/** Response for `POST /v2/scores/calculate` — the scores written this run, plus a skipped count. */
exports.CalculateScoresResponse = typebox_1.Type.Object({
    scored: typebox_1.Type.Array(exports.PartnerScoreWithRatingsTransport),
    /** Haulers visited but below minimum activity (no score written). */
    skipped: typebox_1.Type.Number(),
});
/** Querystring for listing `score_rating_config` rows. The violation form fetches `manuallyReportable=true`. */
exports.ListScoreRatingConfigsQuerystring = typebox_1.Type.Object({
    serviceType: typebox_1.Type.Optional(exports.ScoreServiceType),
    manuallyReportable: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
});
//# sourceMappingURL=score-transport.js.map